<template>
  <el-col :span="24" id="pettycash" v-loading.body="changingPage && !navigating" element-loading-text="Fetching page...">
    <ListViewHeader
      title="PETTY CASH"
      ref="listViewHeader"
      :meta="meta"
      :selection="selection"
      :currentPage="currentPage"
      :changePage="changePage"
      :handleBatchUpdateSelection="handleBatchUpdateSelection"
      :batchUpdateOptions="batchUpdateOptions"
      :showFilters="showFilters"
      :toggleFilters="toggleFilters"
      :createRouteName="'NewPc'">
    </ListViewHeader>
    <el-row type="flex" justify="left" align="middle" style="padding: 0 12px; margin: 12px 0;">
      <el-col :span="6">
        <el-row type="flex" justify="left" align="middle">
          <el-button-group>
            <el-button
              :type="activeTab === 'open' ? 'primary' : null"
              :class="activeTab === 'open' ? 'focussedBtn' : 'unfocussedBtn'"
              @click="() => changeTab('open')">
              View Open Transactions
            </el-button>
            <el-button
              :type="activeTab === 'all' ? 'primary' : null"
              :class="activeTab === 'all' ? 'focussedBtn' : 'unfocussedBtn'"
              @click="() => changeTab('all')">
              View All Transactions
            </el-button>
          </el-button-group>
        </el-row>
      </el-col>
      <el-col :span="18">
        <Filters
        ref="filters"
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="12"
        :autoApply="false"
        clearAction="clearPcState"
        clearMetadataAction="resetPcMeta"
        searchAction="fetchPcsWithQuery"
        transactionType="Pc"
        :lineFieldSearch="{ field: 'other_name', transactionType: 'pc' }"
        @filterCleared="() => $refs.filters.applyFilters()"
        @doneFiltering="getAppliedFilters">
        <template v-for="(f, i) in filters" slot-scope="props" :slot="`${f.type}${i}`">
          <component :key="i" :is="f.type" :properties="f.props" :ref="`${f.type+'_pc_ref'}`" />
        </template>
      </Filters>
      </el-col>
    </el-row>
    <el-row type="flex" justify="end" align="middle" v-loading="changingTab" element-loading-text="Loading...">
      <el-col>
        <el-row type="flex">
          <TransactionsConfList v-if="pcs" :aggridColumns="aggridColumns" name="pc_transactions_conf_list" ref="transactions_conf_list"
          :rows="pcs" :currentPage="currentPage"/>
        </el-row>
        <el-row v-if="!meta.count" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import store from '@/store/store'
  import { mapGetters } from 'vuex'
  import Filters from '@/components/RetainedFilters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import ListViewHeader from '@/components/DataEntryListViewHeader'
  import { listViewMixins } from '@/Mixins/TransactionListViews'
  import TransactionsConfList from '@/components/SigmaClientNew/TransactionsConfList.vue'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      AutocompleteFilter,
      HeaderInfoText,
      ListViewHeader,
      TransactionsConfList
    },
    mixins: [ listViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        aggridColumns: ['rowSelect', 'ledgerColumn', 'numberColumn', 'descriptionColumn', 'envelopeAmountColumn', 'batchColumn', 'vendorColumn', 'currencyColumn', 'actionsColumn'],
        activeTab: 'open',
        changingTab: false,
        selectedStatuses: ['DRAFT', 'OPEN'],
        statusOptions: [{label: 'DRAFT', value: 'DRAFT'}, {label: 'OPEN', value: 'OPEN'}]
      }
    },
    methods: {
      getAppliedFilters () {
        this.$store.dispatch('clearSetPcFilterInfo')
        sessionStorage.setItem('paginationQuery', JSON.stringify({page: this.currentPage, ...this.$refs.filters.getFilters}))
        this.$store.dispatch('setPcFilterInfo', { page: this.currentPage, ...this.$refs.filters.getFilters })
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selection])
      },
      async changeTab (tab) {
        this.changingTab = true
        this.activeTab = tab
        try {
          this.$store.dispatch('setActiveTab', tab)
          await Promise.all([
            this.$store.dispatch('resetPcMeta'),
            this.$store.dispatch('clearPcState', true),
            this.$store.dispatch('clearFilters'),
            this.$store.dispatch('clearSetPcFilterInfo'),
            this.$store.dispatch('updateTransactionSelection', [])
          ])
          this.selectedStatuses = ['DRAFT', 'OPEN']
          if (tab === 'all') {
            this.selectedStatuses = ['DRAFT', 'OPEN', 'AUDITED', 'POSTED', 'VOIDED', 'CLOSED']
          }
          this.statusOptions = this.selectedStatuses.map(s => ({ label: s, value: s }))

          // Clear Status Filter
          this.$refs.selectFilter_pc_ref[0].selectedVal = this.selectedStatuses
          this.$refs.selectFilter_pc_ref[0].change()

          // Clear Currency Filter
          this.$refs.selectFilter_pc_ref[1].selectedVal = []
          this.$refs.selectFilter_pc_ref[1].change()

          // Clear Search Filter
          this.$refs.keywordFilter_pc_ref[0].keyword = ''
          this.$refs.keywordFilter_pc_ref[0].change()

          // Set selected statuses based on the current tab
          let query = { status__in: this.selectedStatuses }
          await this.$store.dispatch('setFilter', query)
          await this.$store.dispatch('setApFilterInfo', { query })
          sessionStorage.setItem('paginationQuery', JSON.stringify({query}))
          await this.$refs.filters.applyFilters()
        } catch (e) {
          console.log(e)
        } finally {
          this.changingTab = false
        }
      },
      /**
       * Handles paginating between PC pages
       * @param page  Target page
       */
      async changePage (page) {
        try {
          await this.$store.dispatch('setPcFilterInfo', { ...this.$refs.filters.getFilters, ...{ page: page } })
          let filterStoreData = this.getPcFilterInfo
          if (Object.keys(filterStoreData).length) {
            this.filterRetained()
            return
          }
          this.changingPage = true
          const { currentFilters } = this
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { query: { ...currentFilters, page }, storeLines: false })
            : Object.assign({}, { query: { page }, storeLines: false })
          await this.$store.dispatch('clearPcState', [])
          await this.$store.dispatch('setApFilterInfo', { page: page, ...this.currentFilters })
          const entities = await this.$store.dispatch('fetchPcsWithQuery', payload)
          if (entities) {
            const promises = await this.$store.dispatch('findDependencies', entities)
            await Promise.all(promises)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          this.$refs['transactions_conf_list'].restoreSelection(this.selection)
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      },
      async filterRetained () {
        this.changingPage = true
        // Get FilterInfo from the store data
        let filterStoreData = this.$store.getters.getPcFilterInfo
        // Set status dropdown options (all statuses remain in the dropdown)
        if (!Object.keys(filterStoreData).length) {
          const query = { status__in: this.selectedStatuses }
          for (let f in this.currentFilters) {
            if (!query.hasOwnProperty(f)) {
              query[f] = this.currentFilters[f]
            }
          }
          const entities = await store.dispatch('fetchPcsWithQuery', { query, storeLines: false })
          await store.dispatch('setFilter', query)
          sessionStorage.setItem('paginationQuery', JSON.stringify({page: this.currentPage, ...query}))
          await this.$store.dispatch('setPcFilterInfo', { ...query, ...{ page: this.currentPage } })
          const promises = await store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          this.changingPage = false
          return
        } else {
          sessionStorage.setItem('paginationQuery', JSON.stringify({page: this.currentPage, ...filterStoreData}))
        }
        try {
          const { currentFilters } = this
          // Prepare payload query to append to the API
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { query: { ...currentFilters, page: filterStoreData['page'] ? filterStoreData['page'] : this.currentPage }, storeLines: false })
            : Object.assign({}, { query: { page: filterStoreData['page'] ? filterStoreData['page'] : this.currentPage }, storeLines: false })
          // Check 'keyword' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['keyword'] && filterStoreData['keyword'].length > 0) {
            payload.query['search'] = filterStoreData['keyword']
          }
          // Check 'status__in' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['status__in'] && filterStoreData['status__in'].length > 0) {
            payload.query['status__in'] = filterStoreData['status__in']
          }
          // Check 'batch' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['batch'] && filterStoreData['batch'].length > 0) {
            payload.query['batch'] = filterStoreData['batch']
          }
          // Check 'sourcecode' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['sourcecode'] && filterStoreData['sourcecode'].length > 0) {
            payload.query['sourcecode'] = filterStoreData['sourcecode']
          }
           // Check 'vendor' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['batch__bank__currency__in'] && filterStoreData['batch__bank__currency__in'].length > 0) {
            payload.query['batch__bank__currency__in'] = filterStoreData['batch__bank__currency__in']
          }
          // Reset PC data
          await Promise.all([
            store.dispatch('resetPcMeta'),
            store.dispatch('clearPcState', true),
            store.dispatch('fetchUiSetting', { name: 'DEFAULT_PC_CUSTODIAN' }),
            store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
          ])
          // Get PC's data based on the filter query
          const entities = await store.dispatch('fetchPcsWithQuery', payload)
          const promises = await store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          // Prefill the data for the filters
          if (this.filters && this.filters.length && this.$refs) {
            this.$nextTick(() => {
              // Check 'keywordFilter_pc_ref' is exist or not, if exists then assing the value to prefill it
              if (this.$refs && this.$refs.keywordFilter_pc_ref && filterStoreData['keyword'] && filterStoreData['keyword'].length > 0) {
                this.$refs.keywordFilter_pc_ref[0].keyword = filterStoreData['keyword']
                this.$refs.keywordFilter_pc_ref[0].change()
              }
              // Check 'selectFilter_pc_ref' is exist or not, if exists then assing the value to prefill it
              if (this.$refs && this.$refs.selectFilter_pc_ref && filterStoreData['status__in'] && filterStoreData['status__in'].length > 0) {
                this.$refs.selectFilter_pc_ref[0].selectedVal = filterStoreData['status__in']
                this.$refs.selectFilter_pc_ref[0].change()
              }
               // Check 'selectFilter_pc_ref' is exist or not, if exists then assing the value to prefill it
              if (this.$refs && this.$refs.selectFilter_pc_ref && filterStoreData['batch__bank__currency__in'] && filterStoreData['batch__bank__currency__in'].length > 0) {
                this.$refs.selectFilter_pc_ref[1].selectedVal = filterStoreData['batch__bank__currency__in']
                this.$refs.selectFilter_pc_ref[1].change()
              }
              // Check 'autocompleteFilter_pc_ref' is exist or not, if exists then assing the value to prefill it
              if (this.$refs && this.$refs.autocompleteFilter_pc_ref && filterStoreData && ((filterStoreData['batch'] && filterStoreData['batch'] !== '') || (filterStoreData['sourcecode'] && filterStoreData['sourcecode'] !== ''))) {
                // Check 'batch' data is exist or not, if exists then assing the value to prefill it
                if (filterStoreData && filterStoreData['batch']) {
                  let batchInfo = this.getBatchById([filterStoreData['batch']])
                  this.$refs.autocompleteFilter_pc_ref[0].keyword = batchInfo.name
                  this.$refs.autocompleteFilter_pc_ref[0].handleSelect({label: batchInfo.name, value: batchInfo.id})
                }
                // Check 'sourcecode' data is exist or not, if exists then assing the value to prefill it
                if (filterStoreData && filterStoreData['sourcecode']) {
                  let sourceCodeInfo = this.getSourcecodeById([filterStoreData['sourcecode']])
                  this.$refs.autocompleteFilter_pc_ref[1].keyword = sourceCodeInfo.name
                  this.$refs.autocompleteFilter_pc_ref[1].handleSelect({label: sourceCodeInfo.name, value: sourceCodeInfo.id})
                }
              }
            })
          }
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      }
    },
    computed: {
      ...mapGetters({
        pcs: 'getPcs',
        meta: 'getPcMetadata',
        currentPage: 'getCurrentPcPage',
        getBatchById: 'getBatchById',
        getVendorById: 'getVendorById',
        selection: 'getSelectedTransactions',
        getCurrencyByBatchId: 'getCurrencyByBatchId',
        getActiveTab: 'getActiveTab',
        getcurrentFilters: 'getFilters',
        getPcFilterInfo: 'getPcFilterInfo'
      }),
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'status',
              clearable: true,
              opts: this.statusOptions || [],
              defaults: this.selectedStatuses || [],
              multi: true,
              placeholder: 'Status'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'batch',
              remote: true,
              clearable: true,
              action: 'fetchBatchesWithQuery',
              clearAction: 'clearBatchState',
              getter: 'getBatchesBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Batch',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'sourcecode',
              remote: true,
              clearable: true,
              action: 'fetchSourcecodesWithQuery',
              clearAction: 'clearSourcecodeState',
              getter: 'getSourcecodesBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Source code',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'batch__bank__currency',
              clearable: true,
              opts: this.currencyOptions,
              multi: true,
              placeholder: 'Currency',
              styles: '0 4px'
            }
          }
        ]
      },
      currencyOptions () {
        return this.$store.getters.getAllCurrencies.map(c => ({ label: c.code, value: c.id }))
      }
    },
    created () {
      this.activeTab = this.getActiveTab
    },
    mounted () {
      const tableHeight = () => {
        const parentHeight = document.getElementById('pettycash').offsetHeight
        const headerHeight = document.getElementById('list-view-header').offsetHeight
        return parentHeight - headerHeight
      }
      this.tableHeight = tableHeight()
    },
    beforeDestroy () {
      this.$store.dispatch('updateTransactionSelection', [])
      this.$store.dispatch('updateFilteredTransactionSelection', [])
    },
    async beforeRouteEnter (to, from, next) {
      try {
        await Promise.all([
          store.dispatch('resetPcMeta'),
          store.dispatch('clearPcState', true),
          store.dispatch('fetchUiSetting', { name: 'DEFAULT_PC_CUSTODIAN' }),
          store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
        ])
      } catch (e) {
        console.log(e)
      } finally {
        next(vm => {
          vm.filterRetained()
        })
      }
    },
    async beforeRouteLeave (to, from, next) {
      try {
        await store.dispatch('clearPoState', true)
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    }
  }
</script>

<style scoped>
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
</style>


// WEBPACK FOOTER //
// src/components/RouteComponents/PettyCash/PettyCash.vue